import React, { ReactElement, useEffect, useState } from 'react';
import { KatAlert, KatSpinner } from '@amzn/katal-react';
import { ProfileStatus, Redirects } from 'src/types/UserInfo';
import { useRouter } from 'next/router';
import { USER_PROFILE_ONBOARDING } from 'src/constants/pages';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { fetchUserInfo } from 'src/redux/slices/userInfo';
import { AppState } from 'src/redux/store';
import FetchStatus from 'src/types/FetchStatus';
import { Metrics, isStatusLoading } from 'src/utils/';
import { InitializationStatus } from 'src/utils/applicationMetrics';
import weblab from 'src/weblab/weblabService';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { USER_TOKEN } from 'src/utils/request';
import WeblabId from 'src/weblab/weblabId';
import WeblabTreatment from 'src/weblab/weblabTreatment';

const UserInfo = ({ children }: { children: ReactElement }) => {
  const { t } = useTranslation();
  const { push, pathname } = useRouter();
  const userInfoStatus = useAppSelector(({ userInfo }: AppState) => userInfo.status);
  const profileStatus = useAppSelector(({ userInfo }: AppState) => userInfo.profileStatus);
  const redirectTo = useAppSelector(({ userInfo }: AppState) => userInfo.redirectTo);
  const userToken = useAppSelector(({ userInfo }: AppState) => userInfo.userToken);
  const dispatch = useAppDispatch();
  const [metricInitialized, setMetricInitialized] = useState(false);
  useEffect(() => {
    if (userInfoStatus !== FetchStatus.complete) {
      const isPersistedPreference = weblab.checkIfTreatmentEquals(
        WeblabId.SELLER_FORUMS_USER_PREF_STORAGE,
        WeblabTreatment.T1,
      );
      dispatch(fetchUserInfo({ isPersistedPreference }));
    }
    weblab.fetchTreatments();
  }, []);

  const logInitializationMetric = (status: InitializationStatus) => {
    if (!metricInitialized) {
      const metrics = new Metrics();
      metrics.publishInitializationMetric(status);
      setMetricInitialized(true);
    }
  };

  if (userInfoStatus === FetchStatus.complete && redirectTo) {
    const { search, origin, pathname: windowPathname } = window.location;
    if (redirectTo === Redirects.discourse) {
      window.location.assign(`${origin}/forums`);
    }
    if (redirectTo === Redirects.partnerPicker) {
      const currentPath = encodeURIComponent(`${windowPathname}${search}`);
      const redirectURL = `${origin}/authorization/select-account?returnTo=${currentPath}`;
      window.location.assign(redirectURL);
    }
  }
  if (userInfoStatus === FetchStatus.complete
    && profileStatus === ProfileStatus.unregistered
    && pathname !== USER_PROFILE_ONBOARDING
  ) {
    logInitializationMetric('success');
    push(USER_PROFILE_ONBOARDING);
    return <KatSpinner />;
  }

  if (isStatusLoading(userInfoStatus) || redirectTo) {
    return <div data-testid="user-info-spinner"><KatSpinner /></div>;
  }
  if (userInfoStatus === FetchStatus.failed) {
    logInitializationMetric('failure');
    return (
      <KatAlert
        variant="warning"
        header={t('request_userProfileStatusRequestFailureHeader')}
        description={t('reload_page')}
        className="container mt-5"
        persistent
      />
    );
  }
  logInitializationMetric('success');
  return (
    <>
      <Helmet>
        <meta name={USER_TOKEN} content={userToken} />
      </Helmet>
      {children}
    </>
  );
};
export default UserInfo;
